'use es6';

import { Map as ImmutableMap } from 'immutable';
import { rethrowError } from 'UIComponents/core/PromiseHandlers';
import { withDefaultAdapters } from './adapter/defaultAdapters';
import DSAdapterType from '../propTypes/DSAdapterType';
import DSContext from './DSContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import always from 'transmute/always';
import buildAdapter from './adapter/buildAdapter';
import debounce from 'transmute/debounce';
import invariant from 'react-utils/invariant';
import memoize from 'transmute/memoize';
import omit from 'transmute/omit';
import { jsx as _jsx } from "react/jsx-runtime";
const fetchFailedMessage = /*#__PURE__*/_jsx(FormattedMessage, {
  message: "customerDataFilters.MissingField.fetchFailed"
});
const handleFetchHashFunc = (args, assetFamily) => {
  return `${assetFamily}_${JSON.stringify(omit(['getMissingFieldErrorMessage'], args))}`;
};
class DSProvider extends PureComponent {
  constructor(props) {
    super(props);
    this.handleFetchComplete = (assetFamily, result) => {
      const {
        adapter
      } = this.props;
      const {
        hasMore,
        offset,
        updateImmediate
      } = result;
      const prevData = this.draftData.get(assetFamily) || ImmutableMap();
      const newData = adapter.updater(result, prevData, assetFamily);
      this.draftData = this.draftData.set(assetFamily, newData);
      if (updateImmediate) {
        this.updateData();
      } else {
        this.scheduleUpdate();
      }
      return {
        data: newData,
        hasMore,
        offset
      };
    };
    this.handleFetch = memoize(this._handleFetch.bind(this), handleFetchHashFunc);
    this.state = {
      data: ImmutableMap(),
      fetch: this.handleFetch
    };
    this.getMissingFieldErrorMessage = always(fetchFailedMessage);
    this.scheduleUpdate = debounce(props.debounceTime, this.updateData.bind(this));
    this.draftData = ImmutableMap();
  }
  componentDidUpdate(prevProps) {
    const {
      debounceTime
    } = this.props;
    if (debounceTime !== prevProps.debounceTime) {
      this.scheduleUpdate = debounce(debounceTime, this.updateData.bind(this));
    }
  }
  _handleFetch(args, assetFamily) {
    const {
      getMissingFieldErrorMessage = this.getMissingFieldErrorMessage,
      hasMore,
      ids,
      offset,
      query
    } = args;
    const {
      adapter
    } = this.props;
    const fetcher = adapter.getFetcherFromProps(assetFamily, this.props);
    invariant(fetcher, `No fetcher found for type ${assetFamily}`);
    return adapter.fetch(fetcher, {
      getMissingFieldErrorMessage,
      hasMore,
      ids,
      offset,
      query
    }, assetFamily).then(result => {
      // Option to skip callback
      if (result.ignoreCallback) {
        return Promise.resolve(result);
      }
      return this.handleFetchComplete(assetFamily, result);
    }).catch(rethrowError);
  }
  updateData() {
    this.setState({
      data: this.draftData
    });
  }
  render() {
    return /*#__PURE__*/_jsx(DSContext.Provider, {
      value: this.state,
      children: this.props.children
    });
  }
}
DSProvider.defaultProps = {
  adapter: buildAdapter(),
  debounceTime: 50
};
DSProvider.propTypes = {
  adapter: DSAdapterType.isRequired,
  children: PropTypes.node.isRequired,
  debounceTime: PropTypes.number.isRequired
};
export default withDefaultAdapters(DSProvider);